// Joins a run of sibling blocks (`block+`) into markdown. Shared by the doc,
// toggle contents, callout contents and footnote definitions.
//
// Why one place: `renderTableToMarkdown` always wraps a table in its own leading
// and trailing "\n". Joining siblings with "\n\n" puts three blank lines between
// two adjacent tables, and reparsing synthesizes an empty paragraph there, so the
// canonical form is no longer a fixed point. The same holds at every level.

import type { JSONContent, MarkdownRendererHelpers } from "@tiptap/core";
import { TOP_LEVEL_LIST_TYPES } from "./list-blocks";
import { hasTableOptions } from "./table";

function isListType(type: string | undefined): boolean {
  return type !== undefined && TOP_LEVEL_LIST_TYPES.includes(type);
}

/**
 * Ordered list numbers are not kept in the document (list-blocks.ts), so the
 * `start` is passed here, for writing only. `listItem`'s renderer numbers from
 * the parent's `start` plus the item index, which gives "1. 2. 3.".
 */
function withStart(child: JSONContent, ordinal: number): JSONContent {
  if (child.type !== "orderedList" || ordinal === 1) return child;
  return { ...child, attrs: { ...child.attrs, start: ordinal } };
}

/**
 * Joins sibling blocks. The separator is "\n\n" except:
 *
 * - two adjacent tables: "" (each table's own line breaks make exactly one
 *   blank line), or "\n" when the second has an option line - otherwise the
 *   comment touches the first table's last row and GFM reads it as another row;
 * - two lists of the same type: "\n" (they were one list before items became
 *   blocks; a blank line would make it a loose list and wrap items in `<p>`).
 */
export function renderBlockSiblings(
  children: readonly JSONContent[],
  h: MarkdownRendererHelpers,
): string {
  if (children.length === 0) {
    return "";
  }

  // `renderChild` is optional in the public type (MarkdownManager always sets it).
  const renderChild = h.renderChild;
  if (!renderChild) {
    return h.renderChildren([...children], "\n\n");
  }

  /** The next number of the ordered list run going on. */
  let ordinal = 1;
  const advance = (child: JSONContent) => {
    ordinal += child.content?.length ?? 1;
  };

  let out = renderChild(children[0], 0);
  advance(children[0]);
  for (let i = 1; i < children.length; i++) {
    const previous = children[i - 1];
    const current = children[i];
    const sameList = isListType(current.type) && previous.type === current.type;
    const bothTables = previous.type === "table" && current.type === "table";
    const separator = bothTables
      ? hasTableOptions(current)
        ? "\n"
        : ""
      : sameList
        ? "\n"
        : "\n\n";
    if (!sameList) ordinal = 1;
    out += separator + renderChild(withStart(current, ordinal), i);
    advance(current);
  }
  return out;
}
